import React, { useMemo } from 'react';
import { useAuthQuery as useQuery } from '../../../../../shared/hooks/useAuthQuery';
import { api } from '../../../../../convex/_generated/api';
import { Spinner } from '../../../../../catalyst-components/spinner';
import { DailyStatsChart } from '../../../../../shared/components/DailyStatsChart';
import { HorizontalBarChart } from './HorizontalBarChart';

// Convert weekly data {week, rate} to DailyStatsChart-compatible {date, count}
function weeklyRateToChartData(weeklyData: Array<{ week: string; rate: number }>): Array<{ date: string; count: number }> {
    return weeklyData.map(d => ({
        date: isoWeekToDate(d.week),
        count: d.rate,
    }));
}

function weeklyCountToChartData(weeklyData: Array<{ week: string; [key: string]: any }>, field: string): Array<{ date: string; count: number }> {
    return weeklyData.map(d => ({
        date: isoWeekToDate(d.week),
        count: d[field] as number,
    }));
}

// Convert ISO week string (2024-W03) to a date string for the chart
function isoWeekToDate(isoWeek: string): string {
    const [yearStr, weekStr] = isoWeek.split('-W');
    const year = parseInt(yearStr);
    const week = parseInt(weekStr);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    return monday.toISOString().split('T')[0];
}

export const EngagementTab: React.FC = () => {
    const sessionData = useQuery(api.admin_engagement_analytics.getSessionCompletionRate);
    const checkinData = useQuery(api.admin_engagement_analytics.getCheckinStats);
    const actionData = useQuery(api.admin_engagement_analytics.getActionItemStats);
    const chatData = useQuery(api.admin_analytics.getEvergreenMessageStats);

    const isLoading = !sessionData || !checkinData || !actionData || !chatData;

    // Aggregate chat data to weekly for display
    const chatWeeklyData = useMemo(() => {
        if (!chatData?.dailyData) return [];
        const weekMap = new Map<string, number>();
        for (const d of chatData.dailyData) {
            const week = isoWeekToDate(getISOWeekFromDate(d.date));
            weekMap.set(week, (weekMap.get(week) || 0) + d.count);
        }
        return Array.from(weekMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, count]) => ({ date, count }));
    }, [chatData]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    const totalChatMessages = chatData.userStats?.reduce((sum: number, u: any) => sum + u.messageCount, 0) || 0;
    const uniqueChatUsers = chatData.userStats?.length || 0;

    return (
        <div className="space-y-8">
            {/* Session Completion Rate */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Session Completion</h3>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Total Sessions: </span>
                        <span className="font-semibold text-foreground">{sessionData.totalSessions}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Completed: </span>
                        <span className="font-semibold text-foreground">{sessionData.completedSessions}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Completion Rate: </span>
                        <span className="font-semibold text-foreground">{sessionData.overallRate}%</span>
                    </div>
                </div>
                <DailyStatsChart
                    data={weeklyRateToChartData(sessionData.weeklyData)}
                    title="Weekly Session Completion Rate"
                    lineColor="#10b981"
                    yAxisLabel="Rate %"
                    tooltipSuffix="% completion"
                />
            </div>

            {/* Check-in Completion vs Dismissal */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Check-in Engagement</h3>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Completed: </span>
                        <span className="font-semibold text-green-600">{checkinData.totals.completed}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Dismissed: </span>
                        <span className="font-semibold text-amber-600">{checkinData.totals.dismissed}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Pending: </span>
                        <span className="font-semibold text-muted-foreground">{checkinData.totals.pending}</span>
                    </div>
                </div>
                <DailyStatsChart
                    data={weeklyRateToChartData(checkinData.weeklyData.map(w => ({ week: w.week, rate: w.completionRate })))}
                    title="Weekly Check-in Completion Rate"
                    lineColor="#f59e0b"
                    yAxisLabel="Rate %"
                    tooltipSuffix="% completion"
                />
                <HorizontalBarChart
                    data={[
                        { label: 'Completed', value: checkinData.totals.completed },
                        { label: 'Dismissed', value: checkinData.totals.dismissed },
                        { label: 'Pending', value: checkinData.totals.pending },
                    ]}
                    title="Check-in Status Breakdown"
                    sortBy="value"
                />
            </div>

            {/* Evergreen Chat Engagement */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Evergreen Chat</h3>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Total Messages: </span>
                        <span className="font-semibold text-foreground">{totalChatMessages}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Unique Users: </span>
                        <span className="font-semibold text-foreground">{uniqueChatUsers}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Avg Messages/User: </span>
                        <span className="font-semibold text-foreground">
                            {uniqueChatUsers > 0 ? Math.round(totalChatMessages / uniqueChatUsers) : 0}
                        </span>
                    </div>
                </div>
                {chatWeeklyData.length > 0 && (
                    <DailyStatsChart
                        data={chatWeeklyData}
                        title="Weekly Chat Messages"
                        lineColor="#8b5cf6"
                        yAxisLabel="Messages"
                        tooltipSuffix="messages"
                    />
                )}
            </div>

            {/* Action Item Follow-through */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Action Item Follow-through</h3>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Total Items: </span>
                        <span className="font-semibold text-foreground">{actionData.totalItems}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Completed: </span>
                        <span className="font-semibold text-green-600">{actionData.completedItems}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Follow-through Rate: </span>
                        <span className="font-semibold text-foreground">{actionData.completionRate}%</span>
                    </div>
                </div>
                <DailyStatsChart
                    data={weeklyRateToChartData(actionData.weeklyData)}
                    title="Weekly Action Item Completion Rate"
                    lineColor="#06b6d4"
                    yAxisLabel="Rate %"
                    tooltipSuffix="% completed"
                />
            </div>
        </div>
    );
};

// Helper to convert a date string to ISO week
function getISOWeekFromDate(dateStr: string): string {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const yearStart = new Date(d.getFullYear(), 0, 4);
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + yearStart.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}
